import React, { useState, useEffect } from 'react';
import { db, storage, auth } from '../../utils/firebase';
import { collection, addDoc, getDocs, serverTimestamp, query, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import { Product, ProductColor, ProductVariant, Brand, Category } from '../../types';
import { Plus, Upload, Loader2, LogOut, X, Check, RefreshCw, Trash2, Edit2, RotateCcw, Tag } from 'lucide-react';
import { compressImage } from '../../utils/imageOptimizer';
import CategoryManager, { DEFAULT_CATEGORIES } from './CategoryManager';

// Memoized Row Component to prevent re-renders
const ProductTableRow = React.memo(({ product, editingId, onEdit, onDelete }: { 
    product: Product; 
    editingId: string | null; 
    onEdit: (p: Product) => void; 
    onDelete: (id: string) => void; 
}) => (
    <tr className={`hover:bg-gray-50 ${editingId === product.id ? 'bg-blue-50' : ''}`}>
        <td className="p-4 w-24">
        <img 
            src={product.image} 
            alt={product.title} 
            className="w-16 h-16 object-cover bg-gray-100"
            loading="lazy"
        />
        </td>
        <td className="p-4">
        <h4 className="font-heading text-lg">{product.title}</h4>
        <span className="text-xs text-gray-500 uppercase tracking-wider">{product.category}</span>
        {product.description && <p className="text-sm text-gray-600 mt-1 line-clamp-1">{product.description}</p>}
        </td>
        <td className="p-4 text-right">
        <div className="flex flex-col items-end gap-2">
            <span className="font-body font-bold text-gray-800">£{product.price ? product.price.toFixed(2) : '0.00'}</span>
            <div className="flex gap-2 mt-1">
                <button 
                    onClick={() => onEdit(product)}
                    className="text-blue-500 hover:text-blue-700"
                    title="Edit Product"
                >
                    <Edit2 size={16} />
                </button>
                <button 
                    onClick={() => onDelete(product.id)}
                    className="text-red-400 hover:text-red-600"
                    title="Delete Product"
                >
                    <Trash2 size={16} />
                </button>
            </div>
        </div>
        </td>
    </tr>
));

const ProductList = React.memo(({ products, editingId, onEdit, onDelete }: {
    products: Product[];
    editingId: string | null;
    onEdit: (p: Product) => void;
    onDelete: (id: string) => void;
}) => (
    <div className="bg-white border border-gray-200">
        <table className="w-full text-left">
        <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
            <th className="p-4 font-heading text-sm text-gray-600">IMAGE</th>
            <th className="p-4 font-heading text-sm text-gray-600">DETAILS</th>
            <th className="p-4 font-heading text-sm text-gray-600 text-right">ACTIONS</th>
            </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
            {products.map((product) => (
                <ProductTableRow 
                    key={product.id} 
                    product={product} 
                    editingId={editingId} 
                    onEdit={onEdit} 
                    onDelete={onDelete} 
                />
            ))}
            {products.length === 0 && (
            <tr>
                <td colSpan={3} className="p-8 text-center text-gray-500 font-body">
                No products found. Add one to get started.
                </td>
            </tr>
            )}
        </tbody>
        </table>
    </div>
));

const AdminInventory: React.FC = () => {
  const { logout } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [colorUploading, setColorUploading] = useState(false); // For individual color uploads

  const [editingId, setEditingId] = useState<string | null>(null);

  // Category State (managed via CategoryManager, stored in Firestore)
  const [categories, setCategories] = useState<Category[]>([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  // Form State
  const [title, setTitle] = useState('');
  const [series, setSeries] = useState(''); // New State for Series
  const [brand, setBrand] = useState<Brand | undefined>(undefined); // Brand field
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('Popsicle');
  const [description, setDescription] = useState('');
  const [dropDate, setDropDate] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  // Optional landscape crop — shown full-bleed by the Latest page on wide screens
  const [wideImageFile, setWideImageFile] = useState<File | null>(null);
  const [existingWideImageUrl, setExistingWideImageUrl] = useState('');

  // Tier & Window State
  const [tier, setTier] = useState<Product['tier'] | ''>('');
  const [fragmentNumber, setFragmentNumber] = useState('');
  const [windowOpenAt, setWindowOpenAt] = useState('');
  const [windowCloseAt, setWindowCloseAt] = useState('');
  const [leadTimeText, setLeadTimeText] = useState('');
  const [provenanceLine, setProvenanceLine] = useState('');

  // Apparel Specific State
  const [stock, setStock] = useState('');
  const [launchSeason, setLaunchSeason] = useState('');
  const [gender, setGender] = useState<'Men' | 'Women' | undefined>(undefined);
  // const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [productColors, setProductColors] = useState<ProductColor[]>([]);
  const [variants, setVariants] = useState<ProductVariant[]>([]);

  // Gallery State
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);


  // Details & Specs State
  const [detailsText, setDetailsText] = useState(''); // Newline separated
  // Purchase options for non-apparel products (deck sizes, variants) — saved
  // to `sizes` so the product page's option selector appears and the choice
  // flows through basket, checkout and orders
  const [optionsText, setOptionsText] = useState(''); // Comma separated
  const [specs, setSpecs] = useState<{key: string, value: string}[]>([{key: '', value: ''}]);
  
  // Temporary state for adding a new color
  const [newColorName, setNewColorName] = useState('');
  const [newColorFile, setNewColorFile] = useState<File | null>(null);
  const [newColorBackFile, setNewColorBackFile] = useState<File | null>(null); // Back image
  const [newColorGalleryFiles, setNewColorGalleryFiles] = useState<File[]>([]); // Gallery images
  const colorInputRef = React.useRef<HTMLInputElement>(null);
  const colorBackInputRef = React.useRef<HTMLInputElement>(null);
  const colorGalleryInputRef = React.useRef<HTMLInputElement>(null);

  const availableSizes = ['XS', 'SM', 'MED', 'LG', 'XL', 'XXL'];

  // Dropdown options: Firestore categories, falling back to the built-in defaults
  // until categories have been created/imported.
  const categoryOptions: { name: string; isApparel?: boolean }[] =
    categories.length > 0 ? categories : DEFAULT_CATEGORIES;

  const isApparel = categoryOptions.find(c => c.name === category)?.isApparel
    ?? ['Tee shirt', 'Sweatshirt', 'Hoodies', 'Bottoms'].includes(category);

  const fetchProducts = async () => {
    const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const productList: Product[] = [];
    querySnapshot.forEach((doc) => {
      productList.push({ id: doc.id, ...doc.data() } as Product);
    });
    setProducts(productList);
  };

  const fetchCategories = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'categories'));
      const categoryList: Category[] = [];
      querySnapshot.forEach((doc) => {
        categoryList.push({ id: doc.id, ...doc.data() } as Category);
      });
      categoryList.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
      setCategories(categoryList);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  // Once real categories load, make sure the current selection is valid
  // (unless editing an existing product, where legacy categories are kept).
  useEffect(() => {
    if (!editingId && categories.length > 0 && !categories.some(c => c.name === category)) {
      setCategory(categories[0].name);
    }
  }, [categories, editingId]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };



  const handleAddColor = async () => {
    if (!newColorName) {
        alert("Please provide a name for the color variant (e.g. 'Midnight Blue').");
        return;
    }
    if (!newColorFile) {
        alert("Please upload an image for this color variant.");
        return;
    }
    
    setColorUploading(true);
    try {
        const compressedFile = await compressImage(newColorFile);
        const storageRef = ref(storage, `products/colors/${Date.now()}_${newColorFile.name}`);
        await uploadBytes(storageRef, compressedFile);
        const imageUrl = await getDownloadURL(storageRef);

        let backImageUrl = '';
        if (newColorBackFile) {
             const compressedBack = await compressImage(newColorBackFile);
             const backRef = ref(storage, `products/colors/${Date.now()}_back_${newColorBackFile.name}`);
             await uploadBytes(backRef, compressedBack);
             backImageUrl = await getDownloadURL(backRef);
        }

        const galleryUrls: string[] = [];
        for (const file of newColorGalleryFiles) {
            let fileToUpload = file;
            if (file.type.startsWith('image/')) {
                fileToUpload = await compressImage(file);
            }
            
            // Lowercase the extension — the storefront detects videos by '.mp4' in the URL
            const safeName = file.name.replace(/\.[^.]+$/, ext => ext.toLowerCase());
            const refName = `products/colors/gallery/${Date.now()}_${safeName}`;
            const sRef = ref(storage, refName);
            await uploadBytes(sRef, fileToUpload as Blob);
            const url = await getDownloadURL(sRef);
            galleryUrls.push(url);
        }
        
        // 1. Add the new Color
        setProductColors(prev => [...prev, { 
            name: newColorName, 
            image: imageUrl, 
            backImage: backImageUrl,
            gallery: galleryUrls 
        }]);
        
        // 2. Initialize variants for this color (one for each available size)
        const newVariantsForColor = availableSizes.map(size => ({
            id: `${newColorName}-${size}`,
            color: newColorName,
            size: size,
            stock: 0
        }));
        
        // 3. Merge with existing variants
        setVariants(prev => [...prev, ...newVariantsForColor]);

        setNewColorName('');
        setNewColorFile(null);
        setNewColorBackFile(null);
        setNewColorGalleryFiles([]);
        if (colorInputRef.current) colorInputRef.current.value = '';
        if (colorBackInputRef.current) colorBackInputRef.current.value = '';
        if (colorGalleryInputRef.current) colorGalleryInputRef.current.value = '';
    } catch (error) {
        console.error("Error uploading color image:", error);
        alert("Failed to upload color image. Check console for details.");
    } finally {
        setColorUploading(false);
    }
  };

  const handleRemoveColor = (index: number) => {
    const colorToRemove = productColors[index];
    setProductColors(prev => prev.filter((_, i) => i !== index));
    // Remove functionality related to this color's variants
    setVariants(prev => prev.filter(v => v.color !== colorToRemove.name));
  };

  // Direct Variant Update by ID
  const handleVariantChange = (id: string, val: string) => {
      const num = parseInt(val) || 0;
      setVariants(prev => prev.map(v => v.id === id ? { ...v, stock: num } : v));
  };

  // Helper to find stock for a specific color/size combo safely
  const getStockForVariant = (colorName: string, size: string) => {
      const found = variants.find(v => v.color === colorName && v.size === size);
      return found ? found.stock : ''; // Return empty string if 0 for cleaner UI input? No, keeping 0 is explicit.
  };

  const handleVariantStockChange = (id: string, newStock: string) => {
    const val = parseInt(newStock) || 0;
    setVariants(prev => prev.map(v => v.id === id ? { ...v, stock: val } : v));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !price || (!imageFile && !editingId)) {
      alert('Please fill in all required fields. internal image check.');
      return;
    }

    setUploading(true);

    try {
      let imageUrl = '';
      
      // 1. Upload Image (Only if new file provided)
      if (imageFile) {
          const compressedFile = await compressImage(imageFile);
          const storageRef = ref(storage, `products/${Date.now()}_${imageFile.name}`);
          await uploadBytes(storageRef, compressedFile);
          imageUrl = await getDownloadURL(storageRef);
      } else if (editingId) {
          // Keep existing image
          const existing = products.find(p => p.id === editingId);
          imageUrl = existing ? existing.image : '';
      }

      // 1.2 Upload Wide Image (optional; kept unless replaced or removed)
      let wideImageUrl = existingWideImageUrl;
      if (wideImageFile) {
          const compressedWide = await compressImage(wideImageFile);
          const wideRef = ref(storage, `products/wide/${Date.now()}_${wideImageFile.name}`);
          await uploadBytes(wideRef, compressedWide);
          wideImageUrl = await getDownloadURL(wideRef);
      }

      // 1.5 Upload Gallery Images & Videos
      const newGalleryUrls: string[] = [];
      for (const file of galleryFiles) {
        let fileToUpload = file;
        if (file.type.startsWith('image/')) {
            fileToUpload = await compressImage(file);
        }
        // Lowercase the extension — the storefront detects videos by '.mp4' in the URL
        const safeName = file.name.replace(/\.[^.]+$/, ext => ext.toLowerCase());
        const refName = `products/gallery/${Date.now()}_${safeName}`;
        const sRef = ref(storage, refName);
        await uploadBytes(sRef, fileToUpload);
        const url = await getDownloadURL(sRef);
        newGalleryUrls.push(url);
      }
      
      const finalGallery = [...galleryUrls, ...newGalleryUrls];


      // 2. Add or Update in Firestore
      const productData: any = {
        title,
        series,
        brand: brand || null,
        price: parseFloat(price),
        category,
        image: imageUrl,
        hoverImage: imageUrl,
        wideImage: wideImageUrl || null,
        gallery: finalGallery,
        description,
        dropDate: dropDate ? new Date(dropDate).toISOString() : null,
        // Tier & window mechanics (windowOpenAt supersedes dropDate; dropDate kept as fallback/alias)
        tier: tier || null,
        fragmentNumber: fragmentNumber ? parseInt(fragmentNumber) : null,
        windowOpenAt: windowOpenAt ? new Date(windowOpenAt).toISOString() : null,
        windowCloseAt: windowCloseAt ? new Date(windowCloseAt).toISOString() : null,
        leadTimeText: leadTimeText.trim() || null,
        provenanceLine: provenanceLine.trim() || null,
        status: (() => {
            // Fragments/artifacts are made to order — stock never drives sold_out
            const stockSoldOut = (tier !== 'fragment' && tier !== 'artifact') && stock && parseInt(stock) === 0;
            if (stockSoldOut) return 'sold_out';
            const openAt = windowOpenAt || dropDate;
            return (openAt && new Date(openAt) > new Date()) ? 'coming_soon' : 'available';
        })(),
        // Parse Details (split by newline and filter empty)
        details: detailsText.split('\n').filter(line => line.trim() !== ''),
        // Parse Specs (convert array to object)
        specs: specs.reduce((acc, curr) => {
            if (curr.key.trim() && curr.value.trim()) {
                acc[curr.key.trim()] = curr.value.trim();
            }
            return acc;
        }, {} as Record<string, string>),
        updatedAt: serverTimestamp(),
      };

      if (!editingId) {
        productData.createdAt = serverTimestamp();
      }

      if (!isApparel) {
        // Non-apparel purchase options (e.g. deck sizes) — empty clears them
        const parsedOptions = optionsText.split(',').map(o => o.trim()).filter(Boolean);
        productData.sizes = parsedOptions.length > 0 ? parsedOptions : null;
      }

      if (isApparel) {
        // Calculate total available sizes (any size that has at least 1 variant with stock > 0, OR just present in variants?)
        // Let's list all sizes that exist in the variants array (since we create them for every added color)
        // OR better: All sizes that have > 0 stock? 
        // User probably expects the size to show up if they added the color.
        // Let's include all sizes availableTypes.
        productData.sizes = availableSizes; 
        // Actually, cleaner: derive sizes from variants? No, we just support XS-XXL.
        
        productData.colors = productColors;
        // Total Stock Calculation
        const totalStock = variants.length > 0 
            ? variants.reduce((acc, v) => acc + v.stock, 0) 
            : (parseInt(stock) || 0);

        productData.stock = totalStock;
        productData.variants = variants;
        productData.launchSeason = launchSeason;
        productData.gender = gender;
      }

      if (editingId) {
        await updateDoc(doc(db, 'products', editingId), productData);
        alert('Product updated successfully!');
      } else {
        await addDoc(collection(db, 'products'), productData);
        alert('Product added successfully!');
      }

      // 3. Reset Form and Refresh List

      // 3. Reset Form and Refresh List
      setTitle('');
      setPrice('');
      setCategory(categoryOptions[0]?.name || 'Popsicle');
      setDescription('');
      setDropDate('');
      setImageFile(null);
      setWideImageFile(null);
      setExistingWideImageUrl('');
      // Reset Apparel State
      setStock('');
      setLaunchSeason('');
      setLaunchSeason('');
      // setSelectedSizes([]);
      setProductColors([]);
      setVariants([]);
      setEditingId(null);
      setTier('');
      setFragmentNumber('');
      setWindowOpenAt('');
      setWindowCloseAt('');
      setLeadTimeText('');
      setProvenanceLine('');
      setOptionsText('');
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product? This cannot be undone.')) return;
    try {
        console.log("Attempting to delete product:", id);
        await deleteDoc(doc(db, 'products', id));
        
        // Optimistic Update: Remove from local state immediately
        setProducts(prevProducts => prevProducts.filter(p => p.id !== id));
        
        console.log("Product deleted successfully");
        // We can fetch to be safe, but the optimistic update handles the UI instantly
        // fetchProducts(); 
    } catch (error) {
        console.error("Error deleting product:", error);
        alert("Failed to delete product. Check console for details.");
        // If it failed, maybe re-fetch to restore the list state
        fetchProducts();
    }
  };

  const handleEdit = (product: Product) => {
    setEditingId(product.id);
    setTitle(product.title);
    setSeries(product.series || ''); // Populate Edit
    setBrand(product.brand); // Populate Brand
    setPrice(product.price.toString());
    setCategory(product.category);
    setDescription(product.description || '');
    // Handle Drop Date
    const toLocalInput = (iso: string) => {
        // Convert ISO to datetime-local format (YYYY-MM-DDThh:mm)
        const date = new Date(iso);
        date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
        return date.toISOString().slice(0, 16);
    };
    setDropDate(product.dropDate ? toLocalInput(product.dropDate) : '');

    // Tier & Window
    setTier(product.tier || '');
    setFragmentNumber(product.fragmentNumber ? product.fragmentNumber.toString() : '');
    setWindowOpenAt(product.windowOpenAt ? toLocalInput(product.windowOpenAt) : '');
    setWindowCloseAt(product.windowCloseAt ? toLocalInput(product.windowCloseAt) : '');
    setLeadTimeText(product.leadTimeText || '');
    setProvenanceLine(product.provenanceLine || '');

    // Apparel Inputs
    setStock(product.stock ? product.stock.toString() : '');
    setLaunchSeason(product.launchSeason || '');
    setGender(product.gender);
    // setSelectedSizes(product.sizes || []); // Legacy
    setProductColors(product.colors || []);
    // Ensure variants are loaded. If legacy product without variants, we might need to backfill?
    // For now, load existing.
    if (product.variants && product.variants.length > 0) {
        setVariants(product.variants);
    } else if (product.colors && product.colors.length > 0) {
        // Migration: If we have colors but no variants, init them
        const migratedVariants: ProductVariant[] = [];
        product.colors.forEach(c => {
             availableSizes.forEach(s => {
                 migratedVariants.push({ id: `${c.name}-${s}`, color: c.name, size: s, stock: 0 })
             });
        });
        setVariants(migratedVariants);
    }

    // Purchase options (non-apparel) — stored in `sizes`
    setOptionsText(product.sizes ? product.sizes.join(', ') : '');

    // Details & Specs
    setDetailsText(product.details ? product.details.join('\n') : '');
    if (product.specs) {
        setSpecs(Object.entries(product.specs).map(([key, value]) => ({ key, value })));
    } else {
        setSpecs([{key: '', value: ''}]);
    }
    
    // Warn about image re-upload requirement? 
    // Ideally we keep existing image if no new one is uploaded. Refactoring handleSubmit for that:
    // Gallery
    setGalleryUrls(product.gallery || []);
    setGalleryFiles([]);

    // Wide image (landscape crop)
    setWideImageFile(null);
    setExistingWideImageUrl(product.wideImage || '');
  };

  // Override handleSubmit for Image Logic
  // (NOTE: I am modifying handleSubmit logic in the same replacement block above or separately? 
  // I replaced 'handleSubmit' starting at line 131 in a previous chunk? No, I need to update the top of handleSubmit too.)
  
  const resetForm = () => {
      setTitle('');
      setSeries(''); // Reset
      setBrand(undefined); // Reset Brand
      setPrice('');
      setCategory(categoryOptions[0]?.name || 'Popsicle');
      setDescription('');
      setDropDate('');
      setImageFile(null);
      setWideImageFile(null);
      setExistingWideImageUrl('');
      setStock('');
      setLaunchSeason('');
      // setSelectedSizes([]);
      setProductColors([]);
      setVariants([]);
      setEditingId(null);
      setGalleryFiles([]);
      setGalleryUrls([]);
      setTier('');
      setFragmentNumber('');
      setWindowOpenAt('');
      setWindowCloseAt('');
      setLeadTimeText('');
      setProvenanceLine('');
      setOptionsText('');
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8 border-b pb-4 border-gray-200">
        <h2 className="text-3xl font-heading">INVENTORY MANAGEMENT</h2>
        <button 
          onClick={() => logout()} 
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-black font-body"
        >
          <LogOut size={16} /> SIGN OUT
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* ADD PRODUCT FORM */}
        <div className="bg-white p-6 rounded-sm shadow-sm border border-gray-100 h-fit">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading text-xl flex items-center gap-2">
                {editingId ? <Edit2 size={20} /> : <Plus size={20} />} 
                {editingId ? 'EDIT PRODUCT' : 'ADD NEW PRODUCT'}
            </h3>
            {editingId && (
                <button onClick={resetForm} className="text-xs text-gray-500 flex items-center gap-1 hover:text-black">
                    <RotateCcw size={12} /> CANCEL
                </button>
            )}
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">TITLE</label>
              <input 
                type="text" 
                value={title} 
                onChange={(e) => setTitle(e.target.value)} 
                className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                placeholder="Ex: Pilsner Flagship"
              />
            </div>

            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">SERIES / SUBTITLE</label>
              <input
                type="text"
                value={series}
                onChange={(e) => setSeries(e.target.value)}
                className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                placeholder="Ex: Signature Series or Artist Collection"
              />
            </div>

            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">BRAND</label>
              <select
                value={brand || ''}
                onChange={(e) => setBrand(e.target.value as Brand || undefined)}
                className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50 h-[38px]"
              >
                <option value="">No Brand (Legacy)</option>
                <option value="4HVNSK8">4HVNSK8 (Premium)</option>
                <option value="ForHeavenSkate">For Heaven Skate (Lifestyle)</option>
              </select>
              <p className="text-[10px] text-gray-400 mt-1">* Determines which brand section displays this product</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">PRICE (£)</label>
                <input 
                  type="number" 
                  step="0.01" 
                  value={price} 
                  onChange={(e) => setPrice(e.target.value)} 
                  className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                  placeholder="0.00"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-xs font-bold tracking-widest text-gray-500">CATEGORY</label>
                  <button
                    type="button"
                    onClick={() => setShowCategoryManager(prev => !prev)}
                    className="flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-black tracking-wider"
                    title="Add, edit or delete categories"
                  >
                    <Tag size={10} /> MANAGE
                  </button>
                </div>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50 h-[38px]"
                >
                  {/* Keep a product's legacy category selectable even if it was deleted */}
                  {category && !categoryOptions.some(c => c.name === category) && (
                    <option value={category}>{category} (removed)</option>
                  )}
                  {categoryOptions.map((cat) => (
                    <option key={cat.name} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* PURCHASE OPTIONS (non-apparel: deck sizes, variants, etc.) */}
            {!isApparel && (
              <div>
                <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">PURCHASE OPTIONS (COMMA-SEPARATED, OPTIONAL)</label>
                <input
                  type="text"
                  value={optionsText}
                  onChange={(e) => setOptionsText(e.target.value)}
                  className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-white"
                  placeholder='e.g. 8.0", 8.25", 8.5"'
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Options the customer must choose before claiming (e.g. deck size). The choice carries through to the basket, checkout and order. Leave blank for none.
                </p>
              </div>
            )}

            {/* APPAREL SPECIFIC FIELDS */}
            {isApparel && (
                <div className="bg-gray-50 p-4 border border-gray-200 rounded-sm space-y-4">
                    <h4 className="font-heading text-sm text-gray-700 border-b border-gray-200 pb-2">APPAREL OPTIONS</h4>
                    
                    {/* STOCK & SEASON */}
                    <div className="grid grid-cols-2 gap-4">

                        <div>
                            <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">STOCK</label>
                            <input 
                                type="number" 
                                value={variants.length > 0 ? variants.reduce((acc, v) => acc + (v.stock || 0), 0) : stock} 
                                onChange={(e) => setStock(e.target.value)} 
                                disabled={variants.length > 0}
                                className={`w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black ${variants.length > 0 ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'bg-white'}`}
                                placeholder="0"
                            />
                            {variants.length > 0 && <p className="text-[10px] text-gray-400 mt-1">* Calculated from Matrix below</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">SEASON</label>
                            <input 
                                type="text" 
                                value={launchSeason} 
                                onChange={(e) => setLaunchSeason(e.target.value)} 
                                className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-white"
                                placeholder="SS2026"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">SECTION</label>
                        <select 
                            value={gender || ''} 
                            onChange={(e) => setGender(e.target.value as 'Men' | 'Women')} 
                            className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-white"
                        >
                            <option value="" disabled>Select Section</option>
                            <option value="Men">Men</option>
                            <option value="Women">Women</option>
                        </select>
                    </div>

                    {/* COLOR VARIANTS & STOCK MATRIX COMBINED */}
                    <div>
                        <div className="flex justify-between items-end mb-2">
                             <label className="block text-xs font-bold tracking-widest text-gray-500">VARIANTS & INVENTORY</label>
                             <div className="text-[10px] text-gray-400">Total: {variants.reduce((a,v) => a + v.stock, 0)} items</div>
                        </div>

                        {/* List Existing Colors with Inline Stock */}
                        <div className="space-y-4 mb-4">
                            {productColors.map((col, idx) => (
                                <div key={idx} className="bg-white border border-gray-200 p-3 rounded-sm shadow-sm">
                                    <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2">
                                        <div className="flex items-center gap-3">
                                            <div className="flex gap-1">
                                                <img src={col.image} alt={col.name} className="w-10 h-10 object-cover rounded-md border border-gray-100" />
                                                {col.backImage && <img src={col.backImage} alt={`${col.name} Back`} className="w-10 h-10 object-cover rounded-md border border-gray-100" />}
                                                {col.gallery && col.gallery.length > 0 && (
                                                    <div className="flex -space-x-2">
                                                        {col.gallery.map((g, gi) => (
                                                            g.includes('.mp4') ? (
                                                                <video key={gi} src={g} className="w-10 h-10 object-cover rounded-md border border-gray-100 ring-1 ring-white bg-black" />
                                                            ) : (
                                                                <img key={gi} src={g} alt="Gallery" className="w-10 h-10 object-cover rounded-md border border-gray-100 ring-1 ring-white" />
                                                            )
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <span className="text-sm font-bold block">{col.name}</span>
                                                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Color Variant</span>
                                            </div>
                                        </div>
                                        <button type="button" onClick={() => handleRemoveColor(idx)} className="text-red-400 hover:text-red-600 transition-colors p-1">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    
                                    {/* Inline Size Grid */}
                                    <div className="grid grid-cols-6 gap-2">
                                        {availableSizes.map(size => {
                                            const vId = `${col.name}-${size}`;
                                            const currentStock = variants.find(v => v.id === vId)?.stock || 0;
                                            return (
                                                <div key={size} className="text-center">
                                                    <label className="block text-[10px] font-bold text-gray-400 mb-1">{size}</label>
                                                    <input 
                                                        type="number" 
                                                        min="0"
                                                        value={currentStock === 0 ? '' : currentStock} 
                                                        onChange={(e) => handleVariantChange(vId, e.target.value)}
                                                        placeholder="0"
                                                        className={`w-full text-center p-1 border text-sm rounded-sm focus:outline-none focus:border-black transition-colors ${currentStock > 0 ? 'border-gray-800 bg-gray-50 font-bold' : 'border-gray-200 text-gray-400'}`}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                            
                            {productColors.length === 0 && (
                                <div className="text-center p-6 border border-dashed border-gray-300 rounded-sm text-gray-400 text-xs">
                                    No colors added. Add a color logic below to start stocking.
                                </div>
                            )}
                        </div>

                        {/* Add New Color */}
                        <div className="bg-gray-100 p-3 rounded-sm border border-gray-200">
                            <label className="block text-[10px] font-bold tracking-widest text-gray-500 mb-2 uppercase">Add New Color</label>
                            <div className="flex gap-2 mb-2">
                                <input 
                                    type="text" 
                                    value={newColorName}
                                    onChange={(e) => setNewColorName(e.target.value)}
                                    placeholder="Color Name (e.g. Midnight)"
                                    className="flex-1 border border-gray-300 p-2 text-xs focus:border-black focus:outline-none"
                                />
                                <div className="relative overflow-hidden w-24">
                                     <input 
                                        ref={colorInputRef}
                                        type="file" 
                                        accept="image/*"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                setNewColorFile(e.target.files[0]);
                                            }
                                        }}
                                        className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                    />
                                    <div className={`w-full h-full border border-gray-300 bg-white flex items-center justify-center text-gray-400 ${newColorFile ? 'border-green-500 text-green-600 bg-green-50' : ''}`}>
                                        {newColorFile ? <Check size={16} /> : <span className="text-[9px] font-bold">FRONT</span>}
                                    </div>
                                </div>
                                <div className="relative overflow-hidden w-24">
                                     <input 
                                        ref={colorBackInputRef}
                                        type="file" 
                                        accept="image/*"
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                setNewColorBackFile(e.target.files[0]);
                                            }
                                        }}
                                        className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                    />
                                    <div className={`w-full h-full border border-gray-300 bg-white flex items-center justify-center text-gray-400 ${newColorBackFile ? 'border-green-500 text-green-600 bg-green-50' : ''}`}>
                                        {newColorBackFile ? <Check size={16} /> : <span className="text-[9px] font-bold">BACK (OPT)</span>}
                                    </div>
                                </div>
                                <div className="relative overflow-hidden w-24">
                                     <input 
                                        ref={colorGalleryInputRef}
                                        type="file" 
                                        multiple
                                        accept="image/*,video/mp4"
                                        onChange={(e) => {
                                            if (e.target.files) {
                                                setNewColorGalleryFiles(Array.from(e.target.files));
                                            }
                                        }}
                                        className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                    />
                                    <div className={`w-full h-full border border-gray-300 bg-white flex items-center justify-center text-gray-400 ${newColorGalleryFiles.length > 0 ? 'border-green-500 text-green-600 bg-green-50' : ''}`}>
                                        {newColorGalleryFiles.length > 0 ? <div className="flex flex-col items-center"><Check size={16} /><span className="text-[9px]">{newColorGalleryFiles.length} IMAGES</span></div> : <span className="text-[9px] font-bold text-center leading-tight">DETAILS<br/>(OPT)</span>}
                                    </div>
                                </div>
                            </div>
                            <button 
                                type="button"
                                onClick={handleAddColor}
                                disabled={colorUploading}
                                className="w-full bg-black text-white text-xs py-2 hover:bg-gray-800 disabled:opacity-50 transition-colors uppercase tracking-wider font-bold"
                            >
                                {colorUploading ? 'UPLOADING...' : 'ADD COLOR & SET STOCK'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">DESCRIPTION</label>
              <textarea 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
                rows={3}
              />
            </div>
            
            {/* DETAILS & SPECS */}
            <div className="bg-gray-50 p-4 border border-gray-200 rounded-sm space-y-4">
                <h4 className="font-heading text-sm text-gray-700 border-b border-gray-200 pb-2">DETAILS & SPECS</h4>
                
                {/* Details List */}
                <div>
                     <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">PRODUCT DETAILS (Bullet Points)</label>
                     <textarea 
                        value={detailsText} 
                        onChange={(e) => setDetailsText(e.target.value)} 
                        className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-white"
                        rows={4}
                        placeholder="Enter each detail point on a new line.&#10;Example:&#10;Sustainable Canadian Maple&#10;Premium wood top sheet"
                     />
                </div>

                {/* Specs Key-Value */}
                <div>
                    <label className="block text-xs font-bold tracking-widest mb-2 text-gray-500">TECHNICAL SPECS</label>
                    <div className="space-y-2">
                        {specs.map((spec, idx) => (
                            <div key={idx} className="flex gap-2">
                                <input 
                                    type="text" 
                                    value={spec.key}
                                    onChange={(e) => {
                                        const newSpecs = [...specs];
                                        newSpecs[idx].key = e.target.value;
                                        setSpecs(newSpecs);
                                    }}
                                    placeholder="Trait (e.g. Length)"
                                    className="flex-1 border border-gray-300 p-2 text-xs focus:border-black focus:outline-none"
                                />
                                <input 
                                    type="text" 
                                    value={spec.value}
                                    onChange={(e) => {
                                        const newSpecs = [...specs];
                                        newSpecs[idx].value = e.target.value;
                                        setSpecs(newSpecs);
                                    }}
                                    placeholder="Value (e.g. 32)"
                                    className="flex-1 border border-gray-300 p-2 text-xs focus:border-black focus:outline-none"
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newSpecs = specs.filter((_, i) => i !== idx);
                                        setSpecs(newSpecs.length ? newSpecs : [{key: '', value: ''}]);
                                    }}
                                    className="text-gray-400 hover:text-red-500 px-2"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ))}
                        <button 
                            type="button"
                            onClick={() => setSpecs([...specs, {key: '', value: ''}])}
                            className="text-xs font-bold text-blue-600 hover:text-black flex items-center gap-1 mt-2"
                        >
                            <Plus size={12} /> ADD SPEC ROW
                        </button>
                    </div>
                </div>
            </div>

            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">IMAGE</label>
              <div className="border border-dashed border-gray-300 p-4 text-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors relative">
                <input 
                  type="file" 
                  onChange={handleImageChange} 
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/*"
                />
                <div className="flex flex-col items-center justify-center gap-2">
                  <Upload size={20} className="text-gray-400" />
                  <span className="text-xs text-gray-500 font-body">
                    {imageFile ? imageFile.name : (editingId ? 'Upload to Replace Image' : 'Click to upload image')}
                  </span>
                </div>
              </div>
              {editingId && !imageFile && <p className="text-[10px] text-gray-400 mt-1 text-center">Current image will be kept if no new file is uploaded.</p>}
            </div>

            {/* WIDE IMAGE — landscape crop for full-bleed frames (Latest) */}
            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">WIDE IMAGE (LANDSCAPE, OPTIONAL)</label>
              {existingWideImageUrl && !wideImageFile && (
                <div className="relative w-full h-20 mb-2">
                  <img src={existingWideImageUrl} alt="Wide crop" className="w-full h-20 object-cover border border-gray-200" />
                  <button
                    type="button"
                    onClick={() => setExistingWideImageUrl('')}
                    className="absolute top-1 right-1 bg-black text-white rounded-full p-1 hover:bg-red-600"
                    title="Remove wide image"
                  >
                    <X size={10} />
                  </button>
                </div>
              )}
              <div className="border border-dashed border-gray-300 p-4 text-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors relative">
                <input
                  type="file"
                  onChange={(e) => setWideImageFile(e.target.files?.[0] || null)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/*"
                />
                <div className="flex flex-col items-center justify-center gap-2">
                  <Upload size={20} className="text-gray-400" />
                  <span className="text-xs text-gray-500 font-body">
                    {wideImageFile ? wideImageFile.name : (existingWideImageUrl ? 'Upload to Replace Wide Image' : 'Click to upload a landscape crop')}
                  </span>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mt-1 text-center">
                Shown full-bleed on the Latest page on laptop/desktop screens. Without one, the main image is shown whole, letterboxed.
              </p>
            </div>

            {/* GALLERY UPLOAD */}
            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">GALLERY IMAGES & VIDEOS (OPTIONAL)</label>

              {/* Existing Gallery URLs */}
              {galleryUrls.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                   {galleryUrls.map((url, idx) => (
                     <div key={idx} className="relative group w-16 h-16">
                       {url.includes('.mp4') ? (
                         <video src={url} muted className="w-full h-full object-cover border border-gray-200 bg-black" />
                       ) : (
                         <img src={url} alt={`Gallery ${idx}`} className="w-full h-full object-cover border border-gray-200" />
                       )}
                       {url.includes('.mp4') && (
                         <span className="absolute bottom-0 left-0 bg-black/70 text-white text-[8px] font-bold px-1 tracking-wider">VIDEO</span>
                       )}
                       <button
                         type="button"
                         onClick={() => setGalleryUrls(prev => prev.filter((_, i) => i !== idx))}
                         className="absolute top-0 right-0 bg-red-500 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                       >
                         <X size={12} />
                       </button>
                     </div>
                   ))}
                </div>
              )}

              {/* New Files Preview */}
              {galleryFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                   {galleryFiles.map((file, idx) => (
                     <div key={idx} className="relative group w-16 h-16">
                       {file.type.startsWith('video/') ? (
                         <video src={URL.createObjectURL(file)} muted className="w-full h-full object-cover border border-green-200 opacity-80 bg-black" />
                       ) : (
                         <img src={URL.createObjectURL(file)} alt="Preview" className="w-full h-full object-cover border border-green-200 opacity-80" />
                       )}
                       {file.type.startsWith('video/') && (
                         <span className="absolute bottom-0 left-0 bg-black/70 text-white text-[8px] font-bold px-1 tracking-wider">VIDEO</span>
                       )}
                       <button
                         type="button"
                         onClick={() => setGalleryFiles(prev => prev.filter((_, i) => i !== idx))}
                         className="absolute top-0 right-0 bg-red-500 text-white p-0.5"
                       >
                         <X size={12} />
                       </button>
                     </div>
                   ))}
                </div>
              )}

              <div className="border border-dashed border-gray-300 p-4 text-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors relative">
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      const MAX_VIDEO_MB = 100;
                      const accepted: File[] = [];
                      for (const file of Array.from(e.target.files)) {
                        if (file.type.startsWith('video/') && file.type !== 'video/mp4') {
                          alert(`"${file.name}" skipped — videos must be MP4 (the shop only plays .mp4 files).`);
                          continue;
                        }
                        if (file.type === 'video/mp4' && file.size > MAX_VIDEO_MB * 1024 * 1024) {
                          alert(`"${file.name}" skipped — video is larger than ${MAX_VIDEO_MB}MB. Please compress it first.`);
                          continue;
                        }
                        accepted.push(file);
                      }
                      if (accepted.length > 0) setGalleryFiles(prev => [...prev, ...accepted]);
                      e.target.value = '';
                    }
                  }}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/*,video/mp4"
                />
                <div className="flex flex-col items-center justify-center gap-2">
                  <div className="flex gap-1">
                     <Upload size={20} className="text-gray-400" />
                     <Plus size={10} className="text-gray-400" />
                  </div>
                  <span className="text-xs text-gray-500 font-body">
                    Add Gallery Images or Videos (Select Multiple)
                  </span>
                  <span className="text-[10px] text-gray-400 font-body">
                    Videos: MP4 only, max 100MB
                  </span>
                </div>
              </div>
            </div>

            {/* TIER & WINDOW */}
            <div className="bg-gray-50 p-4 border border-gray-200 rounded-sm space-y-4">
              <h4 className="font-heading text-sm text-gray-700 border-b border-gray-200 pb-2">TIER & WINDOW</h4>

              <div>
                <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">TIER</label>
                <select
                  value={tier || ''}
                  onChange={(e) => setTier((e.target.value || '') as Product['tier'] | '')}
                  className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-white h-[38px]"
                >
                  <option value="">None (Legacy)</option>
                  <option value="fragment">Fragment (timed edition)</option>
                  <option value="uniform">Standard Issue (permanent blank)</option>
                  <option value="artifact">Artifact (1 of 1)</option>
                </select>
                <p className="text-[10px] text-gray-400 mt-1">Fragments close permanently at window close. Artifacts sell exactly once. Standard Issue is always available.</p>
              </div>

              {(tier === 'fragment' || tier === 'artifact') && (
                <div>
                  <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">
                    {tier === 'fragment' ? 'FRAGMENT NO.' : 'ARTIFACT NO.'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={fragmentNumber}
                    onChange={(e) => setFragmentNumber(e.target.value)}
                    className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-white"
                    placeholder="e.g. 1"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Catalogue number — used in captions and email subjects ("Fragment 3"). Not an edition count.</p>
                </div>
              )}

              {tier !== 'uniform' && (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">WINDOW OPENS</label>
                    <input
                      type="datetime-local"
                      value={windowOpenAt}
                      onChange={(e) => setWindowOpenAt(e.target.value)}
                      className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-white"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Leave empty for immediate release. Future date shows in "Surfacing Soon".</p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">WINDOW CLOSES</label>
                    <input
                      type="datetime-local"
                      value={windowCloseAt}
                      onChange={(e) => setWindowCloseAt(e.target.value)}
                      className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-white"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">After this, fragments move to the Vault; unclaimed artifacts are withdrawn. Purchases are rejected server-side.</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">LEAD TIME TEXT</label>
                <input
                  type="text"
                  value={leadTimeText}
                  onChange={(e) => setLeadTimeText(e.target.value)}
                  className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-white"
                  placeholder="Made when claimed; allow up to 10 working days."
                />
              </div>

              <div>
                <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">PROVENANCE LINE</label>
                <textarea
                  value={provenanceLine}
                  onChange={(e) => setProvenanceLine(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-white"
                  placeholder="The one-line fiction shown at the top of the product page."
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold tracking-widest mb-1 text-gray-500">DROP DATE (LEGACY, OPTIONAL)</label>
              <input
                type="datetime-local"
                value={dropDate}
                onChange={(e) => setDropDate(e.target.value)}
                className="w-full border border-gray-300 p-2 font-body text-sm focus:outline-none focus:border-brand-black bg-gray-50"
              />
              <p className="text-[10px] text-gray-400 mt-1">Superseded by "Window Opens" — kept as a fallback for existing products.</p>
            </div>

            <button
              type="submit" 
              disabled={uploading}
              className={`w-full text-white py-3 font-heading tracking-widest transition-colors disabled:opacity-50 flex justify-center items-center ${editingId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-brand-black hover:bg-brand-gray'}`}
            >
              {uploading ? <Loader2 className="animate-spin" size={20} /> : (editingId ? 'SAVE CHANGES' : 'ADD PRODUCT')}
            </button>
          </form>
        </div>

        {/* LIST PRODUCTS */}
        <div className="col-span-1 lg:col-span-2">
           {showCategoryManager && (
             <div className="mb-8">
               <CategoryManager
                 categories={categories}
                 onCategoriesChange={fetchCategories}
                 onProductsChange={fetchProducts}
               />
             </div>
           )}
           <h3 className="font-heading text-xl mb-6">EXISTING PRODUCTS</h3>
           <ProductList 
                products={products}
                editingId={editingId}
                onEdit={handleEdit}
                onDelete={handleDelete}
           />
        </div>
      </div>
    </div>
  );
};

export default AdminInventory;
